import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';


const HeaderContainer = styled(motion.header)<{ $hideBorder?: boolean; $scrolled?: boolean; $isMenuOpen?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(10, 10, 10, 0.15);
  backdrop-filter: blur(25px);
  border-bottom: ${props => props.$hideBorder ? 'none' : `1px solid ${props.$scrolled ? 'rgba(64, 224, 255, 0.5)' : 'rgba(64, 224, 255, 0.3)'}`};
  padding: 1.2rem 2rem;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 900px) {
    padding: 1rem 1.5rem;
    background: transparent !important;
    backdrop-filter: none !important;
    border-bottom: none !important;
    box-shadow: none !important;
  }
`;

const Nav = styled.nav`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 2rem;
  background: transparent;

  @media (max-width: 900px) {
    padding: 0 1.5rem;
    background: transparent;
  }
`;

const HomeLink = styled(Link)`
  position: absolute;
  left: 2rem;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 1.3rem 0.55rem 0.8rem;
  border-radius: 999px;
  background: rgba(10, 18, 36, 0.8);
  border: 1px solid rgba(64, 224, 255, 0.45);
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(64, 224, 255, 0.18);
    transform: translateY(-2px);
    color: #ffffff;
  }

  @media (max-width: 1150px) {
    padding: 0.45rem 1rem 0.45rem 0.7rem;
    font-size: 0.9rem;
    gap: 0.5rem;
    left: 1.5rem;
  }

  @media (max-width: 900px) {
    display: none;
  }

  .full-text {
    @media (max-width: 1150px) {
      display: none;
    }
  }

  .short-text {
    display: none;
    
    @media (max-width: 1150px) {
      display: inline;
    }
  }
`;

const LogoMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #40e0ff, #00bfff);
  color: #081224;
  font-size: 0.9rem;
  box-shadow: 0 6px 16px rgba(64, 224, 255, 0.35);
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2.5rem;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;

  @media (max-width: 900px) {
    display: none;
  }
`;

const MobileNavLinks = styled(motion.div)`
  @media (min-width: 901px) {
    display: none !important;
  }
  
  @media (max-width: 900px) {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.95), rgba(10, 10, 30, 0.95));
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 2rem;
    z-index: 1002;
    padding: 2rem;
    pointer-events: auto;
  }
`;

const MobileCloseButton = styled(motion.button)`
  display: none;
  
  @media (max-width: 900px) {
    display: flex;
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: rgba(64, 224, 255, 0.15);
    border: 1px solid rgba(64, 224, 255, 0.4);
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.75rem;
    border-radius: 10px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 10px rgba(64, 224, 255, 0.2);
    z-index: 1003;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(64, 224, 255, 0.25);
      transform: scale(1.05);
      box-shadow: 0 4px 15px rgba(64, 224, 255, 0.3);
    }
  }
`;

const NavLink = styled(motion.button)<{ $isActive: boolean }>`
  background: none;
  border: none;
  color: ${props => props.$isActive ? '#40e0ff' : 'rgba(255, 255, 255, 0.8)'};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  position: relative;
  transition: all 0.3s ease;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;

  .full-text {
    @media (max-width: 480px) {
      display: none;
    }
  }

  .short-text {
    display: none;
    
    @media (max-width: 480px) {
      display: inline;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.$isActive 
      ? 'linear-gradient(135deg, rgba(64, 224, 255, 0.1), rgba(64, 224, 255, 0.05))' 
      : 'transparent'};
    border-radius: 8px;
    transition: all 0.3s ease;
    z-index: -1;
  }

  &:hover::before {
    background: linear-gradient(135deg, rgba(64, 224, 255, 0.15), rgba(64, 224, 255, 0.08));
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0.5rem;
    left: 50%;
    width: ${props => props.$isActive ? '80%' : '0%'};
    height: 2px;
    background: linear-gradient(90deg, #40e0ff, #00bfff);
    transform: translateX(-50%);
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 1px;
    z-index: 0;
  }

  &:hover::after {
    width: 80%;
  }

  @media (max-width: 900px) {
    font-size: 1.2rem;
    padding: 1rem 2rem;
    text-align: center;
    width: 100%;
    max-width: 300px;
    
    &::before {
      border-radius: 12px;
    }

    &::after {
      display: none;
    }
  }
`;

const ActiveIndicator = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(64, 224, 255, 0.1), rgba(64, 224, 255, 0.05));
  border-radius: 8px;
  transition: all 0.3s ease;
  z-index: -1;
`;

const MobileMenuButton = styled(motion.button)<{ $isMenuOpen?: boolean }>`
  display: none;
  background: rgba(64, 224, 255, 0.15);
  border: 1px solid rgba(64, 224, 255, 0.4);
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  position: absolute;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.75rem;
  border-radius: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(64, 224, 255, 0.2);
  z-index: 1003;
  opacity: ${props => props.$isMenuOpen ? 0 : 1};
  pointer-events: ${props => props.$isMenuOpen ? 'none' : 'auto'};

  &:hover {
    background: rgba(64, 224, 255, 0.25);
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 4px 15px rgba(64, 224, 255, 0.3);
  }

  @media (max-width: 900px) {
    display: block;
    right: 1.5rem;
  }
`;

const MobileBackdrop = styled(motion.div)`
  display: none;
  
  @media (max-width: 900px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 998;
  }
`;

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  const sections = useMemo(() => [
    { id: 'home', name: 'Home' },
    { id: 'skills', name: 'Skills' },
    { id: 'projects', name: 'My Work' },
    { id: 'experience', name: 'Experience' }
  ], []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // If we're at the very top, set to home
      if (window.scrollY < 100) {
        setActiveSection('home');
        return;
      }
      
      // Check each section to see which one is currently in view
      let currentSection = 'home';
      
      for (let i = 0; i < sections.length; i++) {
        const element = document.getElementById(sections[i].id);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Section is in view if it's at least 50% visible
          if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
            currentSection = sections[i].id;
            break;
          }
          // If we've scrolled past the top of this section, it's the current one
          else if (rect.top <= 100) {
            currentSection = sections[i].id;
          }
        }
      }
      
      setActiveSection(currentSection);
    };

    // Set initial active section
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  // Close mobile menu when resizing to desktop view
  useEffect(() => {
    const handleResize = () => {
      // Only close if we're actually above 900px (not just a small resize)
      if (window.innerWidth > 900 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    // Debounce resize to prevent rapid firing
    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [isMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    if (sectionId === 'home') {
      // Scroll to top of page for home
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <MobileBackdrop
              key="mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsMenuOpen(false)}
            />
            <MobileNavLinks
              key="mobile-menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, delay: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <MobileCloseButton
                onClick={() => setIsMenuOpen(false)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: -90 }}
                transition={{ duration: 0.2 }}
              >
                <FaTimes />
              </MobileCloseButton>
              <NavLink
                as={Link}
                to="/"
                $isActive={false}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMenuOpen(false)}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.3, 
                  delay: 0.1,
                  ease: "easeOut"
                }}
                style={{
                  background: 'rgba(64, 224, 255, 0.15)',
                  border: '1px solid rgba(64, 224, 255, 0.4)',
                  color: '#40e0ff',
                  fontWeight: 600,
                  marginBottom: '1rem'
                }}
              >
                <LogoMark style={{ width: '28px', height: '28px', fontSize: '0.8rem' }}>
                  <FaArrowLeft />
                </LogoMark>
                ollie-gillyon.com
              </NavLink>
              {sections.map((section, index) => (
                <NavLink
                  key={`mobile-${section.id}`}
                  $isActive={activeSection === section.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => scrollToSection(section.id)}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.3, 
                    delay: (index + 2) * 0.1,
                    ease: "easeOut"
                  }}
                >
                  {section.name}
                </NavLink>
              ))}
            </MobileNavLinks>
          </>
        )}
      </AnimatePresence>

      <HeaderContainer
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        $hideBorder={isMenuOpen}
        $scrolled={scrolled}
        $isMenuOpen={isMenuOpen}
        style={{
          ...(!isMobile ? {
            background: scrolled 
              ? 'rgba(10, 10, 10, 0.25)' 
              : 'rgba(10, 10, 10, 0.15)',
            boxShadow: scrolled 
              ? '0 8px 32px rgba(0, 0, 0, 0.2)' 
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
          } : {
            background: 'transparent',
            boxShadow: 'none',
          }),
          ...(isMenuOpen ? {
            opacity: 0,
            pointerEvents: 'none'
          } : {
            opacity: 1,
            pointerEvents: 'auto'
          })
        }}
      >
        <Nav>
          <HomeLink to="/">
            <LogoMark>
              <FaArrowLeft />
            </LogoMark>
            <span className="full-text">ollie-gillyon.com</span>
            <span className="short-text">OG.com</span>
          </HomeLink>
          <NavLinks>
              {sections.map((section, index) => (
                <NavLink
                  key={section.id}
                  $isActive={activeSection === section.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => scrollToSection(section.id)}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.3, 
                    delay: index * 0.1,
                    ease: "easeOut"
                  }}
                >
                  {section.name}
                  {activeSection === section.id && (
                    <ActiveIndicator
                      layoutId="activeIndicator"
                      initial={false}
                      transition={{ 
                        type: "spring", 
                        stiffness: 500, 
                        damping: 30 
                      }}
                    />
                  )}
                </NavLink>
              ))}
          </NavLinks>
        </Nav>

        <MobileMenuButton 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          $isMenuOpen={isMenuOpen}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          animate={{ rotate: isMenuOpen ? 90 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            animate={{ rotate: isMenuOpen ? 45 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </motion.div>
        </MobileMenuButton>
      </HeaderContainer>
    </>
  );
};

export default Header;
